import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import articleService from '../../services/articleService';
import typeArticleService from '../../services/typeArticleService';
import localService from '../../services/localService';
import TypeArticleCardSelect from './TypeArticleCardSelect';
import LocalSelectDropdown from './LocalSelectDropdown';
import MonthYearPicker from './MonthYearPicker';

const getLastLocal = () => {
  try {
    const interventionRaw = localStorage.getItem('entretienIntervention');
    if (interventionRaw) {
      const intervention = JSON.parse(interventionRaw);
      if (intervention?.id) {
        const val = localStorage.getItem(`lastLocalForIntervention_${intervention.id}`);
        if (val) {
          const num = Number(val);
          return isNaN(num) ? val : num; // Prefer number if possible
        }
      }
    }
  } catch {}
  return '';
};

const ArticleNotFoundModal = ({ isOpen, onClose, codeABarre, onSuccess, initialData = null, mode = 'create' }) => {
  const { isDarkMode } = useTheme();

  // Get lastLocal ON EVERY RENDER so it's available for both getInitialForm and the dropdown
  const lastLocal = getLastLocal();

  const getInitialForm = () => {
    if (initialData) {
      return {
        codeABarre: initialData.codeABarre || codeABarre || '',
        numeroSerie: initialData.numeroSerie || '',
        designation: initialData.designation || '',
        typeArticleId: initialData.typeArticleId || '',
        capacite: initialData.capacite || '',
        marque: initialData.marque || '',
        local: initialData.localId || lastLocal || '',
        emplacement: initialData.emplacement || '',
        pressionPermanente: typeof initialData.pressionPermanente === 'boolean' ? initialData.pressionPermanente : true,
        datetimeFabrication: initialData.datetimeFabrication ? initialData.datetimeFabrication.slice(0, 10) : '',
        observation: initialData.observation || '',
        observationClient: initialData.observationClient || '',
        enService: typeof initialData.enService === 'boolean' ? initialData.enService : true, // Add this field
      };
    }
    return {
      codeABarre: codeABarre || '',
      numeroSerie: '',
      designation: '',
      typeArticleId: '',
      capacite: '',
      marque: '',
      local: lastLocal || '',
      emplacement: '',
      pressionPermanente: true,
      datetimeFabrication: '',
      observation: '',
      observationClient: '',
      enService: true, // Add this field
    };
  };

  const [form, setForm] = useState(getInitialForm);

  // Reset form every time modal opens
  useEffect(() => {
    if (isOpen) {
      setForm(getInitialForm());
    }
    // eslint-disable-next-line
  }, [isOpen, initialData, codeABarre]);

  const [locaux, setLocaux] = useState([]);
  const [loadingLocaux, setLoadingLocaux] = useState(false);
  // Fetch locaux by clientId if intervention is present in localStorage
  useEffect(() => {
    let clientId = null;
    try {
      const interventionRaw = localStorage.getItem('entretienIntervention');
      if (interventionRaw) {
        const intervention = JSON.parse(interventionRaw);
        clientId = intervention?.client?.id;
      }
    } catch {}
    if (clientId) {
      setLoadingLocaux(true);
      localService.getLocauxByClient(clientId).then(res => {
        if (res.success && Array.isArray(res.data)) setLocaux(res.data);
        else setLocaux([]);
      }).finally(() => setLoadingLocaux(false));
    } else {
      setLocaux([]);
    }
  }, [isOpen]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [typeArticles, setTypeArticles] = useState([]);
  const [groupedTypes, setGroupedTypes] = useState({});


  useEffect(() => {
    if (isOpen) {
      fetchTypesWithFamille();
    }
    // eslint-disable-next-line
  }, [isOpen]);

  const fetchTypesWithFamille = async () => {
    setLoading(true);
    try {
      const res = await typeArticleService.getAllTypeArticlesWithFamille();
      const types = res.success && Array.isArray(res.data) ? res.data : [];
      setTypeArticles(types);
      // Group by type.famille.designation
      const grouped = {};
      types.forEach(type => {
        const famName = type.famille && type.famille.designation ? type.famille.designation : 'Autre';
        if (!grouped[famName]) grouped[famName] = [];
        grouped[famName].push(type);
      });
      setGroupedTypes(grouped);
    } catch (err) {
      setError("Erreur lors du chargement des types d'article");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      // Build payload with all form fields for article creation or update
      let clientId = undefined;
      try {
        const interventionRaw = localStorage.getItem('entretienIntervention');
        if (interventionRaw) {
          const intervention = JSON.parse(interventionRaw);
          clientId = intervention?.client?.id;
        }
      } catch {}
      let payload = {
        codeABarre: form.codeABarre,
        numeroSerie: form.numeroSerie,
        designation: form.designation,
        typeArticleId: form.typeArticleId,
        capacite: form.capacite,
        marque: form.marque,
        localId: form.local || undefined,
        emplacement: form.emplacement || undefined,
        pressionPermanente: typeof form.pressionPermanente === 'boolean' ? form.pressionPermanente : undefined,
        datetimeFabrication: form.datetimeFabrication || undefined,
        observation: form.observation || undefined,
        observationClient: form.observationClient || undefined,
        clientId: clientId || undefined,
        enService: typeof form.enService === 'boolean' ? form.enService : true, // Add this field
      };
      let res;
      if (mode === 'edit' && initialData && initialData.id) {
        // Add id to payload for update
        payload = { ...payload, id: initialData.id };
        res = await articleService.updateArticle(initialData.id, payload);
      } else {
        res = await articleService.createArticle(payload);
      }
      if (res.success) {
        // After successful creation or update, fetch the article by code à barre and pass it to onSuccess
        try {
          const getRes = await articleService.getArticleByCodeABarre(form.codeABarre);
          if (onSuccess) onSuccess(getRes && getRes.success ? getRes.data : null);
        } catch {
          if (onSuccess) onSuccess(null);
        }
        if (clientId && form.local) {
          // Save last selected local for this intervention
          const interventionRaw = localStorage.getItem('entretienIntervention');
          if (interventionRaw) {
            const intervention = JSON.parse(interventionRaw);
            if (intervention?.id) {
              localStorage.setItem(
                `lastLocalForIntervention_${intervention.id}`,
                form.local
              );
            }
          }
        }
        onClose();
      } else {
        setError(res.message || (mode === 'edit' ? "Erreur lors de la modification de l'article" : "Erreur lors de la création de l'article"));
      }
    } catch (err) {
      setError(mode === 'edit' ? "Erreur lors de la modification de l'article" : "Erreur lors de la création de l'article");
    } finally {
      setLoading(false);
    }
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className={`rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        {/* Header */}
        <div className={`px-6 py-4 flex-shrink-0 ${
          isDarkMode
            ? 'bg-gray-800'
            : 'bg-gradient-to-r from-primary-600 to-primary-700'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center mr-3">
                <i className="fas fa-barcode text-white text-lg"></i>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">
                  {mode === 'edit' ? "Modifier l'article" : "Nouvel Article (Code non trouvé)"}
                </h2>
                <p className="text-primary-100 text-sm">
                  {mode === 'edit' ? "Modifier les informations de l'équipement" : "Créer un nouvel équipement de sécurité"}
                </p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
            >
              <i className="fas fa-times text-xl"></i>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1" style={{ maxHeight: 'calc(90vh - 140px)' }}>
          <div className="p-6">
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl">
                <div className="flex items-center">
                  <i className="fas fa-exclamation-circle mr-3 text-red-500"></i>
                  <div>
                    <div className="font-medium">Erreur de validation</div>
                    <div className="text-sm">{error}</div>
                  </div>
                </div>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Barcode and Désignation on top */}
              <div className={`rounded-xl p-6 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} mb-2`}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Code à barres *</label>
                    <input type="text" value={form.codeABarre} disabled className={`w-full px-4 py-3 border rounded-lg ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-gray-100 border-gray-300 text-gray-900'}`} />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Numéro</label>
                    <input
                      type="text"
                      value={form.designation}
                      onChange={e => handleChange('designation', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    />
                  </div>
                </div>
              </div>

              {/* TypeArticle selection grouped by familleTypeArticle */}
              <div className={`rounded-xl p-6 ${isDarkMode ? 'bg-gray-700' : 'bg-green-50'} mb-2`}>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Type d'article *</label>
                <TypeArticleCardSelect
                  groupedTypes={groupedTypes}
                  selectedTypeId={form.typeArticleId}
                  onSelect={id => handleChange('typeArticleId', id)}
                />
              </div>

              {/* Other info */}
              <div className={`rounded-xl p-6 ${isDarkMode ? 'bg-gray-700' : 'bg-purple-50'} mb-2`}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Capacité *</label>
                    <input type="text" value={form.capacite} onChange={e => handleChange('capacite', e.target.value)} className={`w-full px-4 py-3 border rounded-lg ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`} required />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Marque</label>
                    <input type="text" value={form.marque} onChange={e => handleChange('marque', e.target.value)} className={`w-full px-4 py-3 border rounded-lg ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`} />
                  </div>
                </div>
                {/* New fields: Local, Emplacement, Pression, Date de fabrication, Observations */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Local *</label>
                    <LocalSelectDropdown
                      value={form.local || ''}
                      onChange={val => handleChange('local', val)}
                      options={locaux}
                      loading={loadingLocaux}
                      disabled={loadingLocaux}
                      isDarkMode={isDarkMode}
                      required
                      defaultValue={lastLocal || ''} // <-- now this works!
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Emplacement *</label>
                    <input type="text" value={form.emplacement || ''} onChange={e => handleChange('emplacement', e.target.value)} className={`w-full px-4 py-3 border rounded-lg ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`} required />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Numéro de série</label>
                    <input
                      type="text"
                      value={form.numeroSerie}
                      onChange={e => handleChange('numeroSerie', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    />
                  </div>
                </div>
                <div className="flex flex-col md:flex-row gap-4 mt-4 items-center">
                  <div className="flex items-center gap-4">
                    <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Pression *</label>
                    <label className="flex items-center gap-1">
                      <input type="radio" name="pressionPermanente" checked={form.pressionPermanente !== false} onChange={() => handleChange('pressionPermanente', true)} required />
                      <span className="text-sm">P.P.</span>
                    </label>
                    <label className="flex items-center gap-1">
                      <input type="radio" name="pressionPermanente" checked={form.pressionPermanente === false} onChange={() => handleChange('pressionPermanente', false)} required />
                      <span className="text-sm">P. aux.</span>
                    </label>
                  </div>
                  <div className="flex items-center gap-4">
                    <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>État *</label>
                    <label className="flex items-center gap-1">
                      <input 
                        type="checkbox" 
                        checked={form.enService}
                        onChange={e => handleChange('enService', e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 h-4 w-4"
                      />
                      <span className="text-sm">En service</span>
                    </label>
                  </div>
                  <div className="flex-1">
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Date de fabrication *</label>
                    <MonthYearPicker
                      value={form.datetimeFabrication}
                      onChange={(value) => handleChange('datetimeFabrication', value)}
                      isDarkMode={isDarkMode}
                      required
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Observations</label>
                  <textarea rows="2" value={form.observation || ''} onChange={e => handleChange('observation', e.target.value)} className={`w-full px-4 py-3 border rounded-lg resize-none ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}></textarea>
                </div>
                <div className="mt-4">
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Observations client</label>
                  <textarea rows="2" value={form.observationClient || ''} onChange={e => handleChange('observationClient', e.target.value)} className={`w-full px-4 py-3 border rounded-lg resize-none ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}></textarea>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Footer - Fixed at bottom */}
        <div className={`border-t px-6 py-4 flex-shrink-0 ${isDarkMode ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-gray-50'}`}>
          <div className="flex flex-col sm:flex-row justify-end gap-3">
             
            <button 
              type="submit"
              onClick={handleSubmit}
              disabled={
                loading ||
                !form.typeArticleId ||
                !form.capacite ||
                !form.local ||
                !form.emplacement ||
                typeof form.pressionPermanente !== 'boolean' ||
                !form.datetimeFabrication
              }
              className={`px-6 py-3 rounded-lg font-medium transition-all ${
                loading ||
                !form.typeArticleId ||
                !form.capacite ||
                !form.local ||
                !form.emplacement ||
                typeof form.pressionPermanente !== 'boolean' ||
                !form.datetimeFabrication
                  ? 'bg-gray-400 cursor-not-allowed text-white'
                  : 'bg-primary-600 hover:bg-primary-700 text-white shadow-lg hover:shadow-xl'
              }`}
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  {mode === 'edit' ? 'Modification...' : 'Création...'}
                </div>
              ) : (
                <div className="flex items-center">
                  <i className="fas fa-save mr-2"></i>
                  {mode === 'edit' ? "Enregistrer les modifications" : "Créer l'article"}
                </div>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticleNotFoundModal;
